import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '../src/context/AuthContext';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import { useToast } from '../src/components/Toast';
import Button from '../src/components/Button';
import ContentContainer from '../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';
import { api } from '../src/api/client';

/** Verify2FAScreen - 6-digit OTP input for two-factor authentication during login. */
export default function Verify2FAScreen() {
  const { email, returnTo } = useLocalSearchParams<{ email: string; returnTo?: string }>();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const refs = useRef<(TextInput | null)[]>([]);
  const router = useRouter();
  const { completeVerification } = useAuth();
  const toast = useToast();
  const { colors } = useTheme();
  const styles = useStyles(colors);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /** Process digit input; supports full-code paste across all 6 fields. */
  const handleDigitChange = (text: string, index: number) => {
    const clean = text.replace(/[^0-9]/g, '');
    if (clean.length > 1) {
      const pasted = clean.slice(0, 6).split('');
      const newDigits = [...digits];
      pasted.forEach((d, i) => { if (i + index < 6) newDigits[i + index] = d; });
      setDigits(newDigits);
      const nextIdx = Math.min(index + pasted.length, 5);
      refs.current[nextIdx]?.focus();
      return;
    }
    const newDigits = [...digits];
    newDigits[index] = clean;
    setDigits(newDigits);
    if (clean && index < 5) refs.current[index + 1]?.focus();
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  /** POST /api/auth/verify-2fa — validate 2FA code and complete auth flow. */
  const handleVerify = async () => {
    const code = digits.join('');
    if (code.length !== 6) { setError('Please enter the full 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api<{ token: string; refreshToken: string; role: string; profile: any }>('/api/auth/verify-2fa', {
        method: 'POST',
        body: { email, code },
        skipAuth: true,
      });
      await completeVerification(res.token, res.refreshToken, res.role as 'owner' | 'user' | 'admin', res.profile);
      router.replace((returnTo || '/') as any);
    } catch (err: any) {
      setError(err.message || 'Invalid code');
    } finally { setLoading(false); }
  };

  /** POST /api/auth/resend-code — resend 2FA code with 60s cooldown. */
  const handleResend = async () => {
    if (cooldown > 0) return;
    try {
      await api('/api/auth/resend-code', { method: 'POST', body: { email, type: '2fa' }, skipAuth: true });
      const RESEND_COOLDOWN_SECONDS = 60;
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.show({ type: 'success', title: 'Code resent!' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <ContentContainer maxWidth={480}>
          <Text style={styles.title}>Two-Factor Authentication</Text>
          <Text style={styles.subtitle}>Enter the login code sent to</Text>
          <Text style={styles.email}>{email}</Text>

          <View style={styles.otpRow}>
            {digits.map((d, i) => (
              <TextInput
                key={i}
                ref={r => { refs.current[i] = r; }}
                style={[styles.otpInput, d ? styles.otpInputFilled : null]}
                value={d}
                onChangeText={text => handleDigitChange(text, i)}
                onKeyPress={e => handleKeyPress(e, i)}
                keyboardType="number-pad"
                maxLength={6}
                selectTextOnFocus
                autoFocus={i === 0}
              />
            ))}
          </View>

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <Button title="Verify" onPress={handleVerify} loading={loading} size="lg" style={styles.verifyBtn} />

          <TouchableOpacity onPress={handleResend} disabled={cooldown > 0} style={styles.resendContainer}>
            <Text style={[styles.resendText, cooldown > 0 && { color: colors.textLight }]}>
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </Text>
          </TouchableOpacity>
        </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, padding: spacing.lg, paddingTop: spacing.xl },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs, textAlign: 'center' },
  subtitle: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center' },
  email: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.primary, textAlign: 'center', marginBottom: spacing.lg },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
  otpRow: { flexDirection: 'row', justifyContent: 'center', gap: 8, marginBottom: spacing.lg },
  otpInput: {
    width: 48, height: 56, borderRadius: borderRadius.md,
    borderWidth: 2, borderColor: c.borderLight,
    backgroundColor: c.surface, textAlign: 'center',
    fontSize: 24, fontWeight: fontWeight.bold, color: c.text,
  },
  otpInputFilled: { borderColor: c.primary },
  verifyBtn: { marginTop: spacing.sm },
  resendContainer: { alignItems: 'center', marginTop: spacing.lg },
  resendText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
